import React, { useEffect, useState } from "react";
import {
  Card,
  Table,
  Button,
  Avatar,
  Space,
  Typography,
  message,
  Tag,
  Popconfirm,
  Grid,
} from "antd";
import {
  SyncOutlined,
  EyeOutlined,
  FacebookFilled,
  DeleteOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Page } from "../../types";
import { pagesService } from "../../services/pages.service";
import { useAuth } from "../../context/AuthContext";

const { Title, Text } = Typography;
const { useBreakpoint } = Grid;

const PagesList: React.FC = () => {
  const [pages, setPages] = useState<Page[]>([]);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const navigate = useNavigate();
  const { user, connectFacebook } = useAuth();
  const screens = useBreakpoint();
  const isMobile = !screens.md;
  const [searchParams, setSearchParams] = useSearchParams();

  const fetchPages = async () => {
    if (!user?.isFacebookConnected) return;
    setLoading(true);
    try {
      const data = await pagesService.getAll();
      setPages(data);
    } catch (error) {
      console.error("Failed to fetch pages", error);
      message.error("Không thể tải danh sách trang");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPages();
  }, [user]);

  useEffect(() => {
    const error = searchParams.get("error");
    const connected = searchParams.get("connected");

    if (error) {
      message.error(decodeURIComponent(error));
      searchParams.delete("error");
      setSearchParams(searchParams, { replace: true });
    }

    if (connected === "true") {
      message.success("Kết nối Facebook thành công!");
      searchParams.delete("connected");
      setSearchParams(searchParams, { replace: true });
      fetchPages();
    }
  }, [searchParams, setSearchParams]);

  const handleSync = async () => {
    if (!user?.isFacebookConnected) {
      message.warning("Vui lòng kết nối tài khoản Facebook trước");
      return;
    }
    setSyncing(true);
    try {
      const result = await pagesService.sync();
      message.success(`Đã đồng bộ thành công ${result.count} trang`);
      // Update list with new data
      setPages(result.pages);
    } catch (error) {
      console.error("Sync failed", error);
      message.error("Không thể đồng bộ trang từ Facebook");
    } finally {
      setSyncing(false);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await pagesService.delete(id);
      message.success("Đã xóa trang khỏi hệ thống");
      fetchPages();
    } catch (error) {
      console.error("Delete failed", error);
      message.error("Không thể xóa trang");
    }
  };

  const columns = [
    {
      title: "Trang",
      key: "name",
      width: isMobile ? 220 : 300,
      render: (_: any, record: Page) => (
        <Space>
          <Avatar
            src={record.pictureUrl}
            shape="square"
            size={isMobile ? "default" : "large"}
          />
          <Space direction="vertical" size={0}>
            <span style={{ fontWeight: 600, fontSize: isMobile ? 13 : 14 }}>
              {record.name}
            </span>
            <span style={{ fontSize: 11, color: "#888" }}>
              ID: {record.pageId}
            </span>
          </Space>
        </Space>
      ),
    },
    {
      title: "Danh mục",
      dataIndex: "category",
      key: "category",
      width: 150,
      render: (text: string) => (
        <Tag style={{ fontSize: isMobile ? 11 : 12 }}>{text}</Tag>
      ),
    },
    {
      title: "Lượt thích",
      dataIndex: "likes",
      key: "likes",
      width: 120,
      render: (likes: number) => (
        <span style={{ fontSize: isMobile ? 12 : 14 }}>
          {(likes || 0).toLocaleString()}
        </span>
      ),
    },
    {
      title: "Hành động",
      key: "action",
      fixed: (isMobile ? "right" : undefined) as any,
      width: isMobile ? 160 : 280,
      render: (_: any, record: Page) => (
        <Space size={isMobile ? 4 : 8}>
          <Button
            icon={<FacebookFilled />}
            size={isMobile ? "small" : "middle"}
            onClick={() =>
              window.open(`https://facebook.com/${record.pageId}`, "_blank")
            }
            title="Truy cập trang Facebook"
          />
          <Button
            type="primary"
            icon={<EyeOutlined />}
            size={isMobile ? "small" : "middle"}
            onClick={() => navigate(`/pages/${record.id}`)}
          >
            {isMobile ? "" : "Quản lý"}
          </Button>
          <Button
            icon={<PlusOutlined />}
            size={isMobile ? "small" : "middle"}
            onClick={() =>
              navigate("/posts/create", { state: { pageIds: [record.id] } })
            }
          >
            {isMobile ? "" : "Tạo bài"}
          </Button>
          <Popconfirm
            title="Xóa trang?"
            description="Bạn có chắc chắn muốn xóa trang này khỏi hệ thống không?"
            onConfirm={() => handleDelete(record.id)}
            okText="Xóa"
            cancelText="Hủy"
            okButtonProps={{ danger: true }}
          >
            <Button
              danger
              icon={<DeleteOutlined />}
              size={isMobile ? "small" : "middle"}
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Space
      direction="vertical"
      size={isMobile ? "middle" : "large"}
      style={{ width: "100%" }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: isMobile ? "flex-start" : "center",
          flexDirection: isMobile ? "column" : "row",
          gap: 16,
        }}
      >
        <div>
          <Title level={isMobile ? 3 : 2} style={{ margin: 0 }}>
            Trang của tôi
          </Title>
          {user?.isFacebookConnected && user.facebookName && (
            <Text type="secondary" style={{ fontSize: isMobile ? 12 : 14 }}>
              Đã kết nối với: <Text strong>{user.facebookName}</Text>
            </Text>
          )}
        </div>
        <Space
          direction={isMobile ? "vertical" : "horizontal"}
          style={{ width: isMobile ? "100%" : "auto" }}
          size={isMobile ? 8 : 12}
        >
          <Button
            type={!user?.isFacebookConnected ? "primary" : "default"}
            icon={<FacebookFilled />}
            onClick={connectFacebook}
            block={isMobile}
            style={
              !user?.isFacebookConnected
                ? { background: "#1877F2", borderColor: "#1877F2" }
                : {}
            }
          >
            {user?.isFacebookConnected
              ? "Cập nhật Facebook"
              : "Kết nối Facebook"}
          </Button>

          {user?.isFacebookConnected && (
            <Button
              icon={<SyncOutlined spin={syncing} />}
              onClick={handleSync}
              loading={syncing}
              block={isMobile}
            >
              Đồng bộ trang
            </Button>
          )}
        </Space>
      </div>

      <Card bodyStyle={{ padding: isMobile ? "12px" : "24px" }}>
        <Table
          columns={columns}
          dataSource={pages}
          rowKey="id"
          loading={loading}
          scroll={{ x: "max-content" }}
          pagination={isMobile ? { size: "small" } : undefined}
        />
      </Card>
    </Space>
  );
};

export default PagesList;
